import type {
  SurveyGroupPreview,
  SurveyGroupPreviewAnswer,
  SurveyGroupPreviewQuestion,
  SurveyGroupResultsSurveyPayload,
} from 'src/types/survey-group-api.types';
import type { QuestionStats, SurveyStats } from 'src/redux/survey/survey.types';
import { SurveyQuestionType } from 'src/redux/survey/survey.types';
import { parsePayloadStatsJson } from '../view-survey/survey-group-results-merge.helpers';

export const CHART_COLORS = ['#3257FB', '#31BB64', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#EF4444', '#64748B'];

export interface SurveyGroupCompareSurveySeries {
  /** Stable key for Recharts `dataKey` (no spaces). */
  dataKey: string;
  /** Short label for legend / tooltip. */
  label: string;
  color: string;
}

/** One row per answer cluster for Recharts; series keys match `SurveyGroupCompareSurveySeries.dataKey`. */
export type SurveyGroupCompareChartRow = Record<string, string | number | undefined> & {
  answerLabel: string;
  /** First non-empty image URL from any visible survey’s mapped answer (for axis thumbnails). */
  answerImageUrl?: string;
};

export interface SurveyGroupCompareQuestionBlock {
  questionClusterId: string;
  title: string;
  questionType: SurveyQuestionType;
  chartRows: SurveyGroupCompareChartRow[];
  /** True when any row has `answerImageUrl` (used for chart bottom margin). */
  hasAnswerImages: boolean;
  series: SurveyGroupCompareSurveySeries[];
}

function slugSurveyKey(extSurveyId: string, index: number): string {
  const short = extSurveyId.replace(/-/g, '').slice(0, 8);
  return `s${index}_${short}`;
}

function questionForCluster(stats: SurveyStats | undefined, cluster: SurveyGroupPreviewQuestion): QuestionStats | undefined {
  if (!stats?.questions?.length) {
    return undefined;
  }
  for (const mqId of cluster.mapped_questions) {
    const q = stats.questions.find((x) => x.id === mqId);
    if (q) {
      return q;
    }
  }
  return undefined;
}

function firstAnswerImageUrlForCluster(
  pa: SurveyGroupPreviewAnswer,
  visiblePayloads: SurveyGroupResultsSurveyPayload[],
  parsedByExt: Map<string, SurveyStats>,
  cluster: SurveyGroupPreviewQuestion
): string | undefined {
  for (const payload of visiblePayloads) {
    const stats = parsedByExt.get(payload.ext_survey_id);
    const q = questionForCluster(stats, cluster);
    const ans = q?.answers.find((a) => pa.mapped_answers.includes(a.id));
    const url = ans?.imageUrl?.trim();
    if (url) {
      return url;
    }
  }
  return undefined;
}

function isFreeformCluster(cluster: SurveyGroupPreviewQuestion, parsedByExt: Map<string, SurveyStats>): boolean {
  for (const [, stats] of parsedByExt) {
    const q = questionForCluster(stats, cluster);
    if (q?.freeformAnswers) {
      return true;
    }
  }
  return false;
}

/**
 * Per-question grouped bar data: each row is an answer cluster; bars are surveys (percentage of respondents choosing that answer).
 */
export function buildSurveyGroupCompareQuestionBlocks(
  visiblePayloads: SurveyGroupResultsSurveyPayload[],
  preview: SurveyGroupPreview | null | undefined
): SurveyGroupCompareQuestionBlock[] {
  if (!preview?.questions?.length || !visiblePayloads.length) {
    return [];
  }

  const parsedByExt = new Map<string, SurveyStats>();
  for (const p of visiblePayloads) {
    const stats = parsePayloadStatsJson(p);
    if (stats) {
      parsedByExt.set(p.ext_survey_id, stats);
    }
  }

  const series: SurveyGroupCompareSurveySeries[] = visiblePayloads.map((p, i) => ({
    dataKey: slugSurveyKey(p.ext_survey_id, i),
    label: p.title?.trim() ? truncateLabel(p.title, 42) : `Survey ${i + 1}`,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const blocks: SurveyGroupCompareQuestionBlock[] = [];

  for (const cluster of preview.questions) {
    if (isFreeformCluster(cluster, parsedByExt)) {
      blocks.push({
        questionClusterId: cluster.id,
        title: cluster.default_title ?? '',
        questionType: SurveyQuestionType.Freeform,
        chartRows: [],
        hasAnswerImages: false,
        series,
      });
      continue;
    }

    const questionType =
      cluster.type === 'multi_select' ? SurveyQuestionType.MultiSelect : SurveyQuestionType.SingleSelect;

    const chartRows: SurveyGroupCompareChartRow[] = [];

    for (const pa of cluster.answers) {
      const row: SurveyGroupCompareChartRow = {
        answerLabel: truncateLabel(pa.default_title ?? '', 80),
      };
      const imgUrl = firstAnswerImageUrlForCluster(pa, visiblePayloads, parsedByExt, cluster);
      if (imgUrl) {
        row.answerImageUrl = imgUrl;
      }
      visiblePayloads.forEach((payload, i) => {
        const stats = parsedByExt.get(payload.ext_survey_id);
        const q = questionForCluster(stats, cluster);
        const ans = q?.answers.find((a) => pa.mapped_answers.includes(a.id));
        const pct = typeof ans?.totalPercentage === 'number' ? Math.round(ans.totalPercentage * 10) / 10 : 0;
        row[series[i].dataKey] = pct;
      });
      chartRows.push(row);
    }

    const hasAnswerImages = chartRows.some((r) => Boolean(r.answerImageUrl));

    blocks.push({
      questionClusterId: cluster.id,
      title: cluster.default_title ?? '',
      questionType,
      chartRows,
      hasAnswerImages,
      series,
    });
  }

  return blocks;
}

function truncateLabel(s: string | null | undefined, max: number): string {
  const t = s ?? '';
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max - 1)}…`;
}
